import {useIntl} from "react-intl";
import {message} from "antd";
import {generateUUID} from "three/src/math/MathUtils";
import {useCreateAssetsQuery} from "hooks";
import {MY_ASSET_CATEGORY} from "utils";
import {uploadAsset, uploadAssetThumbnail} from "api-v3";
import {ASSET_TYPE} from "components/Layout/constants";
import useHandleAssetInfo from "./useHandleAssetInfo";
import usePreProcessAsset from "./usePreProcessAsset";

function useUploadAsset(setUploadProgress, setUploadingAssetInfo) {
  const intl = useIntl();

  const {import3DAsset, getSoundDuration, getVideoDuration, createVideoThumbnail, dataURLtoFile} = usePreProcessAsset();
  const {checkFileType} = useHandleAssetInfo();
  const createMutation = useCreateAssetsQuery();

  const uploadAssetToS3 = (file, urlData) => {
    return new Promise(async (resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", urlData.uploadUrl, true);

      xhr.upload.addEventListener("progress", event => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          if (progress === 100) {
            setUploadProgress(50);
            resolve();
          }
        }
      });

      xhr.onload = () => {
        if (xhr.status === 200) {
          resolve(xhr.responseText);
        } else {
          reject(new Error("Asset upload failed"));
        }
      };

      xhr.onerror = () => {
        reject(new Error("Asset upload failed"));
      };

      xhr.send(file);
    });
  };

  const saveAsset = async (file, urlData, params) => {
    setUploadProgress(90);
    const size = formatFileSize(file.size);

    let {assetType, fileType} = checkFileType(file.name);

    let thumbnailUrl;

    switch (assetType) {
      case ASSET_TYPE.IMAGE:
        thumbnailUrl = urlData.asset.downloadUrl;
        break;
      default:
        thumbnailUrl = urlData?.thumbnail?.downloadUrl || null;
        break;
    }

    let body = {
      url: urlData.asset.downloadUrl,
      thumbnailUrl: thumbnailUrl,
      topCategory: assetType,
      mimetype: fileType,
      size: size,
      name: file.name,
    };

    body = {
      ...body,
      ...params,
    };

    createMutation.mutate(body, {
      onSuccess: () => {
        setUploadProgress(100);
        setUploadingAssetInfo({});
      },
    });
  };

  const uploadVideoAsset = async (file, urlData) => {
    const thumbnail = await createVideoThumbnail(file);
    const duration = await getVideoDuration(file);

    setUploadingAssetInfo({name: file.name, thumbnail, duration});
    setUploadProgress(10);

    const thumbnailImg = dataURLtoFile(thumbnail, "thumbnail");
    const videoReader = new FileReader();
    videoReader.readAsArrayBuffer(file);
    videoReader.onload = async () => {
      try {
        await uploadAssetToS3(file, urlData.asset);
        await uploadAssetToS3(thumbnailImg, urlData.thumbnail);
        await saveAsset(file, urlData, {duration});
      } catch (err) {
        message.error(intl.formatMessage({id: err}));
      }
    };
  };

  const uploadModelAsset = async (file, urlData) => {
    setUploadingAssetInfo({assetType: ASSET_TYPE.MODEL, name: file.name, thumbnail: ""});
    setUploadProgress(10);

    try {
      const url = window.URL.createObjectURL(file);
      const jsonString = await import3DAsset(url);
      await uploadAssetToS3(jsonString, urlData.asset);
      await saveAsset(file, urlData);
    } catch (err) {
      message.error(intl.formatMessage({id: err}));
    }
  };

  const uploadSoundAsset = async (file, urlData) => {
    const duration = await getSoundDuration(file);
    setUploadingAssetInfo({assetType: ASSET_TYPE.SOUND, name: file.name, thumbnail: "", duration});
    setUploadProgress(10);

    const reader = new FileReader();
    reader.readAsArrayBuffer(file);
    reader.onload = async () => {
      try {
        await uploadAssetToS3(file, urlData.asset);
        await saveAsset(file, urlData, {duration});
      } catch (err) {
        message.error(intl.formatMessage({id: err}));
      }
    };
  };

  const uploadImageAsset = async (file, urlData) => {
    setUploadingAssetInfo({thumbnail: URL.createObjectURL(file)});
    setUploadProgress(10);

    try {
      await uploadAssetToS3(file, urlData.asset);
      await saveAsset(file, urlData);
    } catch (err) {
      message.error(intl.formatMessage({id: err}));
    }
  };

  const createAsset = async (file, setCurrentType) => {
    if (file.size > Math.pow(10, 7)) {
      window.alert("You can only upload files up to 10MB.");
      return;
    }

    const assetUUID = generateUUID();
    let {assetType, fileType} = checkFileType(file.name);
    setCurrentType(assetType);

    const thumbnailResponse = await uploadAssetThumbnail({filename: assetUUID});
    const assetResponse = await uploadAsset({filename: assetUUID + "." + fileType});
    const urlData = {asset: assetResponse.data, thumbnail: thumbnailResponse.data};

    switch (assetType) {
      case ASSET_TYPE.MODEL:
        uploadModelAsset(file, urlData);
        break;
      case ASSET_TYPE.IMAGE:
        uploadImageAsset(file, urlData);
        break;
      case ASSET_TYPE.VIDEO:
        uploadVideoAsset(file, urlData);
        break;
      case ASSET_TYPE.SOUND:
        uploadSoundAsset(file, urlData);
        break;
      default:
        break;
    }
  };

  const formatFileSize = bytes => {
    if (bytes >= Math.pow(10, 6)) {
      return (bytes / Math.pow(10, 6)).toFixed(1) + "M";
    } else if (bytes >= Math.pow(10, 3)) {
      return (bytes / Math.pow(10, 3)).toFixed(1) + "K";
    } else {
      return bytes + "B";
    }
  };

  return {
    uploadVideoAsset,
    uploadModelAsset,
    uploadSoundAsset,
    uploadImageAsset,
    createAsset,
  };
}

export default useUploadAsset;
